import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Tag,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import toast from "react-hot-toast";
import { Select } from "../../common";
import { SeparationPageSkeleton } from "../Components/SeparationPageSkeleton";
import Button from "../../common/Button";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Eye,
  ListChecks,
} from "lucide-react";
import {
  type ExitPendingApprovalsQueryArg,
  useExitActionMutation,
  useGetExitPendingApprovalsQuery,
} from "../../../store/apis/exit.api";
import { useGetOrganizationsFilterQuery } from "../../../store/apis/organization.api";
import { useGetMasterConfigByCategoryQuery } from "../../../store/apis/masterConfig.api";
import { MasterConfigCategory } from "../../../constants";
import type { ExitPendingApprovalRow, WorkflowDecision } from "../Utils/types";
import {
  formatExitDateDisplay,
  SEPARATION_DATE_DISPLAY_FORMAT,
  utcYmdFromPickerCurrent,
} from "../Utils/exitDateUtils";
import { getErrorMessage } from "../../../store/utils/apiError";
import { DEBOUNCE_DELAYS } from "../../../utils/debounce";
import { ConfigurableTable, Pagination } from "../../common";
import FilterWrapper from "../../common/FilterWrapper";
import SearchInput from "../../common/SearchInput";
import SimpleTooltip from "../../common/SimpleTooltip";
import type { TableColumn } from "../../common/Table";
import { useAuth } from "../../../store/hooks/useAuth";
import { useHasPermission } from "../../../store/hooks/useRbac";
import { PERMISSIONS } from "../../../utils/rbac/permissions";
import { RoleTypeEnum } from "../../../utils/constants";
import { ExitLifecycleContent } from "../Lifecycle/ExitLifecyclePage";
import TabSelector from "../../employee-service/component/TabSelector";
import ChecklistTemplatesPage from "../Lifecycle/ChecklistTemplatesPage";
import MyExitViewModal from "../Resignation/components/MyExitViewModal";
import {
  exitPendingRowUserCanAct,
  exitRowIsHrReviewPhase,
  exitRowIsManagerApprovalPhase,
} from "../Utils/exitPendingActorUtils";
import { formLabelWithInlineHint } from "../Components/formLabelWithInlineHint";
import { separationFormRequiredMark } from "../Components/separationFormRequiredMark";
import { separationTextAreaClassName } from "../Utils/separationTextAreaClassName";
import { exitWorkflowPhaseKey, humanizeExitStatusLabel } from "../Utils/exitRecordDisplay";
import { SeparationStatusBadge } from "../Components/SeparationStatusBadge";
import { ExitPendingApprovalsTableSkeleton } from "./components/ExitApprovalsTableSkeletons";
import {
  buildEmploymentTypeIdToLabelMap,
  employmentTypeDisplayForRow,
} from "../Utils/employmentTypeDisplay";

dayjs.extend(utc);

function getDisabledDateForExitRow(row: ExitPendingApprovalRow | null) {
  return (current: Dayjs | null): boolean => {
    if (!current) return false;
    if (!row) return current.startOf("day").isBefore(dayjs().startOf("day"));

    const resignationStr = row.resignationDate || row.createdAt;
    const anchor = resignationStr
      ? dayjs.utc(resignationStr).startOf("day")
      : dayjs().startOf("day");

    const selectedUtc = dayjs.utc(current.format("YYYY-MM-DD")).startOf("day");

    if (row.minNoticeEnforced && row.minNoticeCalendarDays) {
      const minLwd = anchor.add(row.minNoticeCalendarDays, "day");
      return selectedUtc.isBefore(minLwd, "day");
    }

    return selectedUtc.isBefore(anchor, "day");
  };
}

type ActionFormValues = {
  decision: WorkflowDecision;
  remarks?: string;
  suggestedLwd?: Dayjs;
  approvedLwd?: Dayjs;
};

/** Display name only in grid and modals (no employee id / code suffix). */
function employeeLabel(row: ExitPendingApprovalRow): string {
  const emp = row.employee;
  const name =
    (typeof row.userName === "string" && row.userName.trim()) ||
    (typeof row.employeeName === "string" && row.employeeName.trim()) ||
    (typeof emp?.fullName === "string" && emp.fullName.trim());
  if (name) return name;
  const id =
    (typeof row.employeeId === "string" && row.employeeId) ||
    (typeof row.userId === "string" && row.userId) ||
    (typeof emp?.userId === "string" && emp.userId) ||
    (typeof emp?.employeeCode === "string" && emp.employeeCode);
  return id || "—";
}

function employeeSearchHaystack(
  row: ExitPendingApprovalRow,
  employmentTypeIdToLabel: Map<string, string>,
): string {
  const emp = row.employee;
  return [
    employeeLabel(row),
    row.employeeId,
    row.userId,
    emp?.userId,
    emp?.employeeCode,
    emp?.workEmail,
    emp?.department,
    emp?.designation,
    employmentTypeDisplayForRow(row, employmentTypeIdToLabel),
    row.reasonText,
    row.workflow?.workflowStatusLabel,
    row.workflow?.workflowStatus,
  ]
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .join(" ")
    .toLowerCase();
}

function rowMatchesSearch(
  row: ExitPendingApprovalRow,
  q: string,
  employmentTypeIdToLabel: Map<string, string>,
): boolean {
  if (!q) return true;
  return employeeSearchHaystack(row, employmentTypeIdToLabel).includes(
    q.trim().toLowerCase(),
  );
}

/** Aligns with GET /v2/exit/pending-approvals exitStatus values (plus legacy phases). */
const EXIT_APPROVALS_STATUS_FILTER_OPTIONS: { value: string; label: string }[] =
  [
    { value: "SUBMITTED", label: "Submitted" },
    { value: "MANAGER_APPROVAL", label: "Manager approval" },
    { value: "MANAGER_APPROVED", label: "Manager approved" },
    { value: "HR_REVIEW", label: "HR review" },
    { value: "NOTICE_PERIOD", label: "Notice period" },
    { value: "EXIT_IN_PROGRESS", label: "Exit in progress" },
    { value: "FINAL_SETTLEMENT", label: "Final settlement" },
    { value: "EXIT_COMPLETED", label: "Exit completed" },
    { value: "REJECTED", label: "Rejected" },
    { value: "WITHDRAWN", label: "Withdrawn" },
  ];

function rowMatchesStatusFilter(
  row: ExitPendingApprovalRow,
  filterValue: string,
): boolean {
  if (!filterValue) return true;
  const phase = exitWorkflowPhaseKey(row);
  if (phase === filterValue) return true;
  if (
    filterValue === "MANAGER_APPROVAL" &&
    (phase === "PENDING_APPROVAL" || phase === "SUBMITTED")
  ) {
    return true;
  }
  return false;
}

/** Mirrors RegularizationManagement accordion sections for list scope UI. */
type ExitApprovalsListAccordionSection = "pending" | "history";

function exitRowStatusDisplay(row: ExitPendingApprovalRow): string {
  const wf = row.workflow;
  const labelRaw =
    (typeof row.statusLabel === "string" && row.statusLabel) ||
    (typeof wf?.workflowStatusLabel === "string" && wf.workflowStatusLabel) ||
    "";
  if (labelRaw) return humanizeExitStatusLabel(labelRaw);
  const code = row.exitStatus ?? row.status ?? wf?.workflowStatus;
  if (code != null && String(code).length > 0) return humanizeExitStatusLabel(String(code));
  return "—";
}

function isPullbackApprovalRequest(row: ExitPendingApprovalRow): boolean {
  return (
    String(row.approverContext?.approvalRequestType ?? "").toUpperCase() ===
    "PULLBACK_REQUEST"
  );
}

function toIsoDateStart(d: Dayjs): string {
  const ymd = utcYmdFromPickerCurrent(d);
  return `${ymd}T00:00:00.000Z`;
}

type PolicyPreviewItem = { key: string; label: string; value: string };

function policyPreviewItems(row: ExitPendingApprovalRow): PolicyPreviewItem[] {
  const ctx = row.approverContext;
  const items: PolicyPreviewItem[] = [];
  // if (typeof ctx?.policyExpectedLastWorkingDay === "string") {
  //   items.push({
  //     key: "policyLwd",
  //     label: "Policy LWD",
  //     value: formatExitDateDisplay(ctx.policyExpectedLastWorkingDay),
  //   });
  // }
  if (typeof row.lastWorkingDayRequested === "string") {
    items.push({
      key: "requestedLwd",
      label: "LWD Requested",
      value: formatExitDateDisplay(row.lastWorkingDayRequested),
    });
  }
  if (typeof row.managerSuggestedLastWorkingDay === "string") {
    items.push({
      key: "mgrSuggested",
      label: "Manager Suggested",
      value: formatExitDateDisplay(row.managerSuggestedLastWorkingDay),
    });
  }
  if (typeof ctx?.noticePeriodCalendarDays === "number") {
    const basis = ctx.noticePeriodDayBasis
      ? ` (${String(ctx.noticePeriodDayBasis).toLowerCase()})`
      : "";
    items.push({
      key: "notice",
      label: "Notice period",
      value: `~${ctx.noticePeriodCalendarDays} calendar days${basis}`,
    });
  }
  return items;
}

function PolicyPreviewBlock({
  row,
  compact = false,
  showTitle = false,
}: {
  row: ExitPendingApprovalRow;
  compact?: boolean;
  showTitle?: boolean;
}) {
  const items = policyPreviewItems(row);
  if (items.length === 0) {
    return compact ? (
      <span className="text-xs text-slate-400">—</span>
    ) : (
      <p className="text-sm text-slate-500 m-0">
        No policy snapshot for this request.
      </p>
    );
  }
  return (
    <div
      className={
        compact
          ? "min-w-0"
          : "rounded-lg border border-slate-200 bg-slate-50 p-3.5 shadow-sm"
      }
    >
      {showTitle ? (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0 mb-2.5">
          Policy preview
        </p>
      ) : null}
      <div
        className={
          compact
            ? "space-y-1 text-xs leading-snug"
            : "space-y-1.5 text-sm leading-snug"
        }
      >
        {items.map((item) => (
          <div key={item.key} className="min-w-0 break-words">
            <span className="font-semibold text-slate-600">{item.label}: </span>
            <span className="text-slate-900 tabular-nums">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function rowEscalatedToHrPool(row: ExitPendingApprovalRow): boolean {
  if (row.lastExitManagerEscalatedToHrPool === true) return true;
  return row.approverContext?.lastExitManagerEscalatedToHrPool === true;
}

const ExitApprovalsPage: React.FC = () => {
  const { user } = useAuth();
  const canHrManage = useHasPermission(PERMISSIONS.EXIT_APPROVALS_MANAGE);
  const hasExitActPermission = useHasPermission(PERMISSIONS.EXIT_APPROVALS);
  const canExitApprovalsView = useHasPermission(PERMISSIONS.EXIT_APPROVALS_VIEW);
  const canActWorkflow = hasExitActPermission || canHrManage;
  /** Backend: EXIT_APPROVALS | EXIT_APPROVALS_VIEW | EXIT_APPROVALS_MANAGE (org Admin / SuperAdmin bypass on server). */
  const canViewExitApprovals =
    hasExitActPermission || canHrManage || canExitApprovalsView;
  const sessionReady = Boolean(user?.id);
  const isSuperAdmin =
    user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase();
  const tokenOrgId = user?.organizationId;

  const [lifecycleExitId, setLifecycleExitId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [approvalsSearch, setApprovalsSearch] = useState("");
  const [activeListAccordion, setActiveListAccordion] =
    useState<ExitApprovalsListAccordionSection>("pending");
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const listScope: "pending" | "history" =
    activeListAccordion === "pending" ? "pending" : "history";
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [superAdminOrgId, setSuperAdminOrgId] = useState<string>("");
  const [approvalTab, setApprovalTab] = useState<"approvalsAndChecklist" | "setup">("approvalsAndChecklist");
  const [viewExit, setViewExit] = useState<ExitPendingApprovalRow | null>(null);
  const [pendingColumnPickerHost, setPendingColumnPickerHost] =
    useState<HTMLDivElement | null>(null);
  const [historyColumnPickerHost, setHistoryColumnPickerHost] =
    useState<HTMLDivElement | null>(null);

  /** SuperAdmin without a session org must pick an org before scope=all can load. */
  const needsOrgPickerForSuperAdmin = isSuperAdmin && !tokenOrgId;


  const skipExitApprovalsLists = !sessionReady || !canViewExitApprovals;

  const historyApprovalsQueryArg = useMemo<ExitPendingApprovalsQueryArg>(
    () => ({
      scope: "history",
      ...(superAdminOrgId ? { organizationId: superAdminOrgId } : {}),
    }),
    [superAdminOrgId],
  );

  const skipHistoryApprovalsList = skipExitApprovalsLists;

  const {
    data: pendingApprovalsPayload,
    isLoading: pendingApprovalsLoading,
    isFetching: pendingApprovalsFetching,
    isError: pendingApprovalsIsError,
    error: pendingApprovalsError,
  } = useGetExitPendingApprovalsQuery(undefined, {
    skip: skipExitApprovalsLists,
  });

  const {
    data: historyApprovalsPayload,
    isLoading: historyApprovalsLoading,
    isFetching: historyApprovalsFetching,
    isError: historyApprovalsIsError,
    error: historyApprovalsError,
  } = useGetExitPendingApprovalsQuery(historyApprovalsQueryArg, {
    skip: skipHistoryApprovalsList,
  });

  const { data: orgFilterData } = useGetOrganizationsFilterQuery(undefined, {
    skip: !sessionReady || !needsOrgPickerForSuperAdmin,
  });

  useEffect(() => {
    if (!hasAutoExpanded && pendingApprovalsPayload && historyApprovalsPayload) {
      if (!pendingApprovalsLoading && !historyApprovalsLoading) {
        if (
          pendingApprovalsPayload.data.length === 0 &&
          historyApprovalsPayload.data.length > 0
        ) {
          setActiveListAccordion("history");
        }
        setHasAutoExpanded(true);
      }
    }
  }, [
    hasAutoExpanded,
    pendingApprovalsPayload,
    historyApprovalsPayload,
    pendingApprovalsLoading,
    historyApprovalsLoading,
  ]);
  const orgFilterOptions = orgFilterData?.data ?? [];

  const { data: employmentTypeMasterList = [] } = useGetMasterConfigByCategoryQuery(
    MasterConfigCategory.EMPLOYMENT_TYPE,
    { skip: !sessionReady || !canViewExitApprovals },
  );

  const employmentTypeIdToLabel = useMemo(
    () => buildEmploymentTypeIdToLabelMap(employmentTypeMasterList),
    [employmentTypeMasterList],
  );

  const [exitAction, { isLoading: acting }] = useExitActionMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<ExitPendingApprovalRow | null>(null);
  const [form] = Form.useForm<ActionFormValues>();

  const approvalTabs = useMemo(() => {
    const tabs: { k: "approvalsAndChecklist" | "setup"; label: string }[] = [
      { k: "approvalsAndChecklist", label: "Approvals & Checklist" },
    ];
    if (canHrManage) {
      tabs.push({
        k: "setup",
        label: "HR Setup (Templates & Interview)",
      });
    }
    return tabs;
  }, [canHrManage]);

  useEffect(() => {
    if (!canHrManage && approvalTab === "setup") {
      setApprovalTab("approvalsAndChecklist");
    }
  }, [canHrManage, approvalTab]);

  useEffect(() => {
    if (approvalTab !== "approvalsAndChecklist") {
      setLifecycleExitId(null);
    }
  }, [approvalTab]);

  const toggleExitListAccordion = (section: ExitApprovalsListAccordionSection) => {
    setActiveListAccordion((prev) =>
      prev === section
        ? section === "pending"
          ? "history"
          : "pending"
        : section,
    );
    setPage(1);
  };

  const pendingFetchedRows = pendingApprovalsPayload?.data ?? [];
  const historyFetchedRows = historyApprovalsPayload?.data ?? [];

  const pendingFilteredRows = useMemo(() => {
    const q = approvalsSearch.trim().toLowerCase();
    let rows = [...pendingFetchedRows].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
    if (q) rows = rows.filter((row) => rowMatchesSearch(row, q, employmentTypeIdToLabel));
    if (statusFilter) {
      rows = rows.filter((row) => rowMatchesStatusFilter(row, statusFilter));
    }
    return rows;
  }, [pendingFetchedRows, approvalsSearch, statusFilter, employmentTypeIdToLabel]);

  const historyFilteredRows = useMemo(() => {
    const q = approvalsSearch.trim().toLowerCase();
    let rows = [...historyFetchedRows].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
    if (q) rows = rows.filter((row) => rowMatchesSearch(row, q, employmentTypeIdToLabel));
    if (statusFilter) {
      rows = rows.filter((row) => rowMatchesStatusFilter(row, statusFilter));
    }
    return rows;
  }, [historyFetchedRows, approvalsSearch, statusFilter, employmentTypeIdToLabel]);

  useEffect(() => {
    setPage(1);
  }, [approvalsSearch, activeListAccordion, statusFilter, superAdminOrgId]);

  const filteredRows =
    listScope === "pending" ? pendingFilteredRows : historyFilteredRows;
  const exitApprovalsColumnPickerHost =
    listScope === "pending" ? pendingColumnPickerHost : historyColumnPickerHost;
  const totalFiltered = filteredRows.length;
  const displayRows = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredRows.slice(start, start + limit);
  }, [filteredRows, page, limit]);

  const lifecycleContextRow = useMemo(() => {
    if (!lifecycleExitId) return null;
    const fromPending = pendingFetchedRows.find((r) => r.id === lifecycleExitId);
    if (fromPending) return fromPending;
    return historyFetchedRows.find((r) => r.id === lifecycleExitId) ?? null;
  }, [lifecycleExitId, pendingFetchedRows, historyFetchedRows]);

  const activeListIsError =
    listScope === "pending" ? pendingApprovalsIsError : historyApprovalsIsError;
  const activeListError =
    listScope === "pending" ? pendingApprovalsError : historyApprovalsError;

  const tableDataLoading =
    listScope === "pending"
      ? pendingApprovalsLoading || pendingApprovalsFetching
      : (historyApprovalsLoading || historyApprovalsFetching) &&
      !skipHistoryApprovalsList;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalFiltered / Math.max(1, limit)));
    if (page > maxPage) setPage(maxPage);
  }, [totalFiltered, limit, page]);

  const rowMayAct = (row: ExitPendingApprovalRow) =>
    exitPendingRowUserCanAct(row, user?.id);

  const openAction = (row: ExitPendingApprovalRow) => {
    setSelectedAction(row);
    form.setFieldsValue({
      decision: "APPROVED",
      remarks: undefined,
      suggestedLwd: undefined,
      approvedLwd: undefined,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedAction(null);
    form.resetFields();
  };

  const submitAction = async () => {
    if (!selectedAction) return;
    try {
      const values = await form.validateFields();
      const body: {
        decision: WorkflowDecision;
        remarks?: string;
        suggestedLastWorkingDay?: string;
        approvedLastWorkingDay?: string;
      } = {
        decision: values.decision,
      };
      const remarksTrim = values.remarks?.trim();
      if (remarksTrim) body.remarks = remarksTrim;
      if (
        values.decision === "APPROVED" &&
        exitRowIsManagerApprovalPhase(selectedAction) &&
        values.suggestedLwd &&
        dayjs.isDayjs(values.suggestedLwd)
      ) {
        body.suggestedLastWorkingDay = toIsoDateStart(values.suggestedLwd);
      }
      if (
        values.decision === "APPROVED" &&
        exitRowIsHrReviewPhase(selectedAction) &&
        values.approvedLwd &&
        dayjs.isDayjs(values.approvedLwd)
      ) {
        body.approvedLastWorkingDay = toIsoDateStart(values.approvedLwd);
      }
      await exitAction({ id: selectedAction.id, body }).unwrap();
      toast.success(
        values.decision === "APPROVED"
          ? "Exit request approved"
          : "Exit request rejected",
      );
      closeModal();
    } catch (e) {
      if (e && typeof e === "object" && "errorFields" in e) {
        return Promise.reject(e);
      }
      toast.error(getErrorMessage(e as never) ?? "The approval action could not be completed");
      return Promise.reject(e);
    }
  };

  const columns: TableColumn<ExitPendingApprovalRow>[] = [
    {
      key: "employee",
      title: "Employee",
      label: "Employee",
      required: true,
      render: (_, record) => (
        <div className="flex flex-col gap-0.5 items-start min-w-0">
          <span className="break-words font-medium text-slate-900">{employeeLabel(record)}</span>
          <span className="text-xs text-slate-500 tabular-nums">
            {record.employee?.employeeCode || record.employeeId || "—"}
          </span>
          {isPullbackApprovalRequest(record) ? (
            <Tag color="blue" className="mt-1 m-0 text-xs shrink-0">
              Pullback Request
            </Tag>
          ) : null}
          {rowEscalatedToHrPool(record) ? (
            <Tag color="orange" className="mt-1 m-0 text-xs shrink-0">
              Escalated to HR pool
            </Tag>
          ) : null}
        </div>
      ),
    },
    {
      key: "status",
      title: "Approval Status",
      label: "Approval Status",
      render: (_, record) => {
        const label = exitRowStatusDisplay(record);
        return (
          <SeparationStatusBadge
            label={label || "—"}
            phase={exitWorkflowPhaseKey(record)}
          />
        );
      },
    },
    {
      key: "policy",
      title: "Policy & dates",
      label: "Policy & dates",
      render: (_, record) => (
        <div className="max-w-[min(14rem,42vw)] min-w-[10.5rem]">
          <PolicyPreviewBlock row={record} compact />
        </div>
      ),
    },
    {
      key: "created",
      title: "Submitted On",
      label: "Submitted On",
      render: (_, record) =>
        record.createdAt
          ? formatExitDateDisplay(record.createdAt)
          : "—",
    },
    {
      key: "employmentType",
      title: "Employment type",
      label: "Employment type",
      defaultVisible: true,
      render: (_, record) =>
        employmentTypeDisplayForRow(record, employmentTypeIdToLabel),
    },
    {
      key: "actions",
      title: "Actions",
      label: "Actions",
      required: true,
      align: "center",
      width: 160,
      render: (_: unknown, record: ExitPendingApprovalRow) => {
        const may = rowMayAct(record);
        const loadChecklist = () => {
          setLifecycleExitId(record.id);
        };
        const iconBtn =
          "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800";
        const showReview = canActWorkflow && may;
        return (
          <div className="flex flex-nowrap items-center justify-center gap-2">
            <SimpleTooltip
              label="View resignation details"
              side="top"
              className="inline-flex shrink-0"
              tooltipClassName="text-xs shadow-md border-0 max-w-xs"
            >
              <button
                type="button"
                onClick={() => setViewExit(record)}
                aria-label="View resignation details"
                className={iconBtn}
              >
                <Eye className="w-4 h-4 text-blue-500" aria-hidden="true" />
              </button>
            </SimpleTooltip>
            <SimpleTooltip
              label="Load checklist and lifecycle."
              side="top"
              className="inline-flex shrink-0"
              tooltipClassName="text-xs shadow-md border-0 max-w-xs"
            >
              <button
                type="button"
                onClick={loadChecklist}
                aria-label="Load checklist and lifecycle"
                className={iconBtn}
              >
                <ListChecks
                  className="w-4 h-4 text-primary-600"
                  aria-hidden="true"
                />
              </button>
            </SimpleTooltip>
            {showReview ? (
              <SimpleTooltip
                label="Review request"
                side="top"
                className="inline-flex shrink-0"
                tooltipClassName="text-xs shadow-md border-0 max-w-xs"
              >
                <button
                  type="button"
                  onClick={() => openAction(record)}
                  aria-label="Review exit request"
                  className={iconBtn}
                >
                  <ClipboardList
                    className="w-4 h-4 text-emerald-600"
                    aria-hidden="true"
                  />
                </button>
              </SimpleTooltip>
            ) : null}
          </div>
        );
      },
    },
  ];

  if (!sessionReady) {
    return <SeparationPageSkeleton />;
  }

  if (!canViewExitApprovals) {
    return (
      <div className="p-4 flex flex-col gap-6 w-full min-w-0">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 m-0">
          Exit Approvals
        </h1>
        <Alert
          showIcon
          type="warning"
          message="Cannot load exit approvals"
          description="This page calls GET /v2/exit/pending-approvals, which requires Exit approvals (act), Exit approvals (view), or Exit approvals (manage). Ask an administrator if you need access."
        />
      </div>
    );
  }

  const exitListAccordionBody = (
    <>
      <FilterWrapper>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-96">
            <SearchInput
              value={approvalsSearch}
              onChange={setApprovalsSearch}
              label="Search"
              placeholder="Search by name, email, department..."
              debounceDelay={DEBOUNCE_DELAYS.SEARCH}
            />
          </div>
          <div className="flex flex-col gap-1.5 w-full sm:w-96 min-w-0">
            <label className="text-sm font-semibold text-slate-700">
              Exit status
            </label>
            <Select
              clearable
              placeholder="All statuses"
              className="w-full"
              value={statusFilter || ""}
              onChange={(v) => setStatusFilter(v ? String(v) : "")}
              options={EXIT_APPROVALS_STATUS_FILTER_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          </div>
        </div>
      </FilterWrapper>

      {activeListIsError ? (
        <Alert
          showIcon
          type="error"
          className="mb-2"
          message={
            listScope === "history"
              ? "Could not load exit requests"
              : "Could not load pending approvals"
          }
          description={
            getErrorMessage(activeListError as never) ??
            "Check permissions (Exit approvals view / act / manage) or organization context and try again."
          }
        />
      ) : null}

      <div className="max-h-[90vh] overflow-y-auto">
        <ConfigurableTable<ExitPendingApprovalRow>
          columns={columns}
          data={displayRows}
          loading={tableDataLoading}
          skeleton={<ExitPendingApprovalsTableSkeleton rows={8} />}
          rowKey="id"
          emptyMessage={
            listScope === "history"
              ? "No exit requests for this view."
              : "No pending exit requests."
          }
          bordered={true}
          hoverable
          spacing={0}
          className="[&_thead_th]:!text-slate-500 [&_th:first-child]:!pl-4 [&_th:first-child]:sm:!pl-6 [&_td:first-child]:!pl-4 [&_td:first-child]:sm:!pl-6 [&_th:last-child]:!pr-4 [&_th:last-child]:sm:!pr-6 [&_td:last-child]:!pr-4 [&_td:last-child]:sm:!pr-6"
          columnPickerPortal
          columnPortalHost={exitApprovalsColumnPickerHost}
          configOptions={{
            persistenceKey: `separation-exit-approvals-v2-${listScope}`,
          }}
        />
      </div>

      {totalFiltered > 0 ? (
        <div className="mt-4">
          <Pagination
            currentPage={page}
            totalItems={totalFiltered}
            itemsPerPage={limit}
            onPageChange={setPage}
            onItemsPerPageChange={(n) => {
              setLimit(n);
              setPage(1);
            }}
          />
        </div>
      ) : null}
    </>
  );

  const approvalsAndChecklistTab = (
    <div className="space-y-6 pt-4">
      {needsOrgPickerForSuperAdmin ? (
        <div className="bg-white rounded-xl shadow-soft border border-slate-200 p-4 space-y-3">
          <Alert
            showIcon
            type="info"
            className="mb-0"
            message="Select an organization"
            description="SuperAdmin users without an organization on the session see all available requests. Select an organization to filter results for a specific entity."
          />
          <div className="flex flex-col gap-1.5 min-w-0 max-w-md">
            <label className="text-sm font-semibold text-slate-700">
              Organization
            </label>
            <Select
              searchable
              placeholder="Choose organization"
              className="w-full"
              value={superAdminOrgId || ""}
              onChange={(v) => setSuperAdminOrgId(v ? String(v) : "")}
              options={orgFilterOptions.map((o) => ({
                value: o.id,
                label: o.organizationName,
              }))}
              clearable
            />
          </div>
        </div>
      ) : null}

      <div className="bg-white rounded-xl shadow-soft border border-slate-200 overflow-hidden">
        <div
          onClick={() => toggleExitListAccordion("pending")}
          className="w-full px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-white cursor-pointer hover:bg-slate-50/40 transition-colors"
        >
          <button
            type="button"
            className="flex-1 text-left text-base font-semibold text-slate-900"
            onClick={() => toggleExitListAccordion("pending")}
          >
            Pending Approvals{!pendingApprovalsLoading ? ` (${pendingFilteredRows.length})` : ''}
          </button>
          <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
            <div
              className="flex items-center justify-center"
              ref={setPendingColumnPickerHost}
            />
            <button
              type="button"
              className="p-1 -mr-1 text-slate-700"
              onClick={() => toggleExitListAccordion("pending")}
              aria-label={
                activeListAccordion === "pending"
                  ? "Collapse pending approvals"
                  : "Expand pending approvals"
              }
            >
              {activeListAccordion === "pending" ? (
                <ChevronUp className="w-5 h-5 shrink-0" aria-hidden />
              ) : (
                <ChevronDown className="w-5 h-5 shrink-0" aria-hidden />
              )}
            </button>
          </div>
        </div>

        {activeListAccordion === "pending" ? (
          <div className="p-6">{exitListAccordionBody}</div>
        ) : null}
      </div>

      <div className="bg-white rounded-xl shadow-soft border border-slate-200 overflow-hidden">
        <div
          onClick={() => toggleExitListAccordion("history")}
          className="w-full px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-white cursor-pointer hover:bg-slate-50/40 transition-colors"
        >
          <button
            type="button"
            className="flex-1 text-left text-base font-semibold text-slate-900"
            onClick={() => toggleExitListAccordion("history")}
          >
            Approval History{!historyApprovalsLoading ? ` (${historyFilteredRows.length})` : ''}
          </button>
          <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
            <div
              className="flex items-center justify-center"
              ref={setHistoryColumnPickerHost}
            />
            <button
              type="button"
              className="p-1 -mr-1 text-slate-700"
              onClick={() => toggleExitListAccordion("history")}
              aria-label={
                activeListAccordion === "history"
                  ? "Collapse approval history"
                  : "Expand approval history"
              }
            >
              {activeListAccordion === "history" ? (
                <ChevronUp className="w-5 h-5 shrink-0" aria-hidden />
              ) : (
                <ChevronDown className="w-5 h-5 shrink-0" aria-hidden />
              )}
            </button>
          </div>
        </div>

        {activeListAccordion === "history" ? (
          <div className="p-6">{exitListAccordionBody}</div>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      <div className="sticky top-0 z-[38] bg-surface-muted px-4 sm:px-6 pt-6 pb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 m-0">
          Exit Approvals
        </h1>
        <div className="mt-4 flex items-center justify-between gap-4">
          <TabSelector
            tabs={approvalTabs}
            active={approvalTab}
            onChange={setApprovalTab}
          />
          {lifecycleExitId && approvalTab === "approvalsAndChecklist" && (
            <Button
              htmlType="button"
              appearance="ghost"
              size="middle"
              onClick={() => setLifecycleExitId(null)}
              icon={<ArrowLeft className="w-4 h-4" aria-hidden />}
              className="!px-4 !py-2 !text-slate-500 hover:!text-slate-700"
            >
              Back
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-6 pt-2 pb-8 max-w-full mx-auto w-full min-w-0 space-y-6">
        {approvalTab === "approvalsAndChecklist" ? (
          lifecycleExitId ? (
            <div className="space-y-4 w-full min-w-0">
              <div className="bg-white rounded-xl shadow-soft border border-slate-200 p-4 sm:p-6 w-full min-w-0">
                <ExitLifecycleContent
                  exitId={lifecycleExitId}
                  variant="full"
                  showHeaderLinks={false}
                  employeeName={lifecycleContextRow ? employeeLabel(lifecycleContextRow) : undefined}
                  employeeCode={lifecycleContextRow?.employee?.employeeCode || lifecycleContextRow?.employeeId}
                  exitStatusLabel={lifecycleContextRow ? exitRowStatusDisplay(lifecycleContextRow) : undefined}
                />
              </div>
            </div>
          ) : (
            approvalsAndChecklistTab
          )
        ) : (
          <div className="w-full min-w-0 pt-4">
            <ChecklistTemplatesPage embedded />
          </div>
        )}
      </div>

      <Modal
        centered
        transitionName=""
        maskTransitionName=""
        title={<span className="text-xl font-semibold text-slate-900">Resignation Approval</span>}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submitAction()}
        okText="Submit"
        confirmLoading={acting}
        destroyOnClose
        width={520}
      >
        <div className="border-t border-slate-200 -mx-6 mb-6" />
        {selectedAction ? (
          <div className="mb-6 grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                Employee Name
              </span>
              <span className="text-[14px] font-medium text-slate-800">
                {employeeLabel(selectedAction)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                Employee ID
              </span>
              <span className="text-[14px] font-medium text-slate-800">
                {selectedAction.employee?.employeeCode || selectedAction.employeeId || "—"}
              </span>
            </div>
            {policyPreviewItems(selectedAction).map((item) => (
              <div key={item.key} className="flex flex-col gap-0.5">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  {item.label}
                </span>
                <span className="text-[14px] font-medium text-slate-800 tabular-nums">
                  {item.value}
                </span>
              </div>
            ))}
            {selectedAction.comments ? (
              <div className="col-span-2 flex flex-col gap-0.5 mt-1">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Comments
                </span>
                <div className="text-[14px] font-medium text-slate-800 whitespace-pre-wrap break-words leading-relaxed bg-slate-50/60 rounded-lg p-3 border border-slate-100">
                  {selectedAction.comments}
                </div>
              </div>
            ) : null}
            {rowEscalatedToHrPool(selectedAction) ? (
              <div className="col-span-2 text-amber-800 text-sm pt-2.5 border-t border-amber-100 mt-1">
                This request reached you after manager escalation (reporting chain
                exhausted → HR pool).
              </div>
            ) : null}
          </div>
        ) : null}

        {/* {selectedAction && exitRowIsManagerApprovalPhase(selectedAction) ? (
          selectedAction.approverContext?.thisRequestHasHrStep === false ? (
            <Alert
              showIcon
              type="warning"
              className="mb-3"
              message="Manager-only approval"
              description="There is no HR step on this request. If you approve a last working date earlier than the policy date, the server will reject the action. In that case, enable an HR approval step for the organization or ask HR to handle early release."
            />
          ) : (
            <Alert
              showIcon
              type="info"
              className="mb-3"
              message="Early release"
              description="If the last working date is earlier than policy would normally allow and an HR approval step exists, HR’s approval confirms that early release."
            />
          )
        ) : null} */}

        <Form
          form={form}
          layout="vertical"
          initialValues={{ decision: "APPROVED" }}
          requiredMark={separationFormRequiredMark}
        >
          <Form.Item
            name="decision"
            label="Action"
            rules={[{ required: true, message: "Select approve or reject" }]}
          >
            <Radio.Group>
              <Radio value="APPROVED">Approve</Radio>
              <Radio value="REJECTED">Reject</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(p, c) => p.decision !== c.decision}>
            {({ getFieldValue }) =>
              getFieldValue("decision") === "APPROVED" &&
                selectedAction &&
                exitRowIsManagerApprovalPhase(selectedAction) ? (
                <Form.Item
                  name="suggestedLwd"
                  label={formLabelWithInlineHint(
                    "Suggest Last working date",
                    "",
                  )}
                  rules={[
                    {
                      required: true,
                      message: "Select suggested last working date",
                    },
                  ]}
                >
                  <DatePicker
                    className="w-full"
                    format={SEPARATION_DATE_DISPLAY_FORMAT}
                    disabledDate={getDisabledDateForExitRow(selectedAction)}
                    style={{ marginTop: "8px" }}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(p, c) => p.decision !== c.decision}>
            {({ getFieldValue }) =>
              getFieldValue("decision") === "APPROVED" &&
                selectedAction &&
                exitRowIsHrReviewPhase(selectedAction) ? (
                <Form.Item
                  name="approvedLwd"
                  label="Final Last Working Day (HR Decision)"
                >
                  <DatePicker
                    className="w-full"
                    format={SEPARATION_DATE_DISPLAY_FORMAT}
                    disabledDate={getDisabledDateForExitRow(selectedAction)}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="remarks"
            label={formLabelWithInlineHint(
              "Remarks",
              "",
            )}
            rules={[
              {
                required: true,
                message: "Remarks are required",
              },
            ]}
          >
            <Input.TextArea
              className={separationTextAreaClassName}
              rows={3}
              placeholder="Remarks"
            />
          </Form.Item>
        </Form>
      </Modal>

      <MyExitViewModal
        exit={viewExit as any}
        onClose={() => setViewExit(null)}
      />
    </>
  );
};

export default ExitApprovalsPage;
